import React, { FC, useState, useEffect } from 'react';
import Form, {
  CheckboxField,
  ErrorMessage,
  Field,
  FormFooter,
  FormHeader,
  FormSection,
  Label,
  ValidMessage,
} from '@atlaskit/form';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import Button, { LoadingButton, ButtonGroup } from '@atlaskit/button';
import Checkbox from '@atlaskit/checkbox';
import Spinner from '@atlaskit/spinner';
import { CheckboxSelect } from '@atlaskit/select';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertableSignalFlowInput } from '@atlassiansox/phobos-client-ts';
import CalendarIcon from '@atlaskit/icon/glyph/calendar';
import AlertIcon from '@atlaskit/icon/glyph/notification-direct';
import GraphLineIcon from '@atlaskit/icon/glyph/graph-line';
import Lozenge from '@atlaskit/lozenge';
import pluralize from 'pluralize';

import { ExperienceErrorBoundary } from '@/components/primitive/error-boundary';
import { WithPageTitle } from '@/components/primitive/with-page-title';
import { FormFieldTooltipLabel } from '@/components/primitive';
import { UFOExperienceContext, useRestartUFOComponentExperience } from '@/common/ufo';
import { simulationViewExperience } from '@/common/ufo/experiences';
import { apiFetch } from '@/services';
import { helperCopy } from '@/components/composed/forms/copy';
import { useFetchErrorController, SignalFlowValidateResultType } from '@/controllers';
import { flexCenter, spaceGapCompact } from '@/styles';
import SfxIcon from '@/components/primitive/sfx-icon';
import { formatDateForDisplay } from '@/utils';
import { DateDisplayFormats } from '@/constants';
import {
  fetchSignalFlowAlertable,
  isSignalFlowValidated,
} from '@/components/composed/forms/slo/fields/utils';

import * as styles from './styles';

const VALUE_IS_REQUIRED = 'This field is required';

const validateRequired = (value?: string) => (value ? undefined : VALUE_IS_REQUIRED);

type ValidationError = {
  errorMessage: string;
};

type QueryValidationResultsProps = {
  errors: ValidationError[] | undefined;
  isValidating: boolean;
  queryType: 'good' | 'bad';
};

const QueryValidationResults: FC<QueryValidationResultsProps> = ({
  errors,
  isValidating,
  queryType,
}) => {
  // Don't show anything while validating or if errors is undefined
  if (isValidating || errors === undefined) return null;

  // Show error messages if there are errors
  if (errors.length > 0) {
    return (
      <ErrorMessage>
        <div>
          {errors.map((err: ValidationError, idx: number) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={`${err.errorMessage}-${idx}`}>{err.errorMessage}</div>
          ))}
        </div>
      </ErrorMessage>
    );
  }

  // Show success message only when errors array exists and is empty
  if (errors.length === 0) {
    return <ValidMessage>Your {queryType} query is valid!</ValidMessage>;
  }

  return null;
};

const createValidationPayload = (
  goodQuery: string,
  badQuery: string,
  objective: string
): AlertableSignalFlowInput => ({
  goodQuery: goodQuery || '',
  badQuery: badQuery || '',
  objective: Number(objective || 0),
});

type SimulationResult = {
  success: boolean;
  data?: any;
  error?: string;
};

type LocationState = {
  publicId?: string;
};

const ALERT_TYPE_OPTIONS = [
  { label: 'Breach', value: 'BREACH' },
  { label: 'Gradual Burn', value: 'GRADUAL' },
  { label: 'Moderate Burn', value: 'MODERATE' },
  { label: 'Steep Burn', value: 'STEEP' },
  { label: 'Ultra-Steep Burn', value: 'ULTRA_STEEP' },
];

const SimulationView: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const publicIdParam = (location.state as LocationState)?.publicId;
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<SimulationResult | null>(null);
  const [sloData, setSloData] = useState<any>(null);
  const [alertTypes, setAlertTypes] = useState<any[]>([]);
  const [alertTypeError, setAlertTypeError] = useState<string>('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<SignalFlowValidateResultType>();

  const { onError } = useFetchErrorController();

  const { data: rawSloData } = useQuery(
    ['slo', publicIdParam],
    () => apiFetch(`/slo/${publicIdParam}`),
    {
      enabled: !!publicIdParam,
    }
  );

  useEffect(() => {
    if (rawSloData?.indicator) {
      setSloData({
        goodQuery: rawSloData.indicator.goodQuery || '',
        badQuery: rawSloData.indicator.badQuery || '',
        objective: rawSloData.objective || '',
        suppressLowTrafficAlerts: rawSloData.suppressLowTrafficAlerts || false,
        lowTrafficThreshold: rawSloData.lowTrafficThreshold || '',
      });
    }
  }, [rawSloData]);

  const handleValidate = async (gQuery: string, bQuery: string, obj: string) => {
    try {
      if (validationError) setValidationError(undefined);
      setIsValidating(true);

      const validatePayload = createValidationPayload(gQuery, bQuery, obj);
      const err = await fetchSignalFlowAlertable(validatePayload);
      setValidationError(err);
    } catch (e) {
      onError(e as Error);
      setValidationError(undefined);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (formData: any): Promise<object | undefined> => {
    try {
      if (!alertTypes || alertTypes.length === 0) {
        setAlertTypeError(VALUE_IS_REQUIRED);
        return { alertTypes: VALUE_IS_REQUIRED };
      }

      // Validate SLO SFx queries before submitting
      const { goodQuery: gQuery, badQuery: bQuery, objective: obj } = formData;
      if (gQuery || bQuery) {
        if (validationError) setValidationError(undefined);
        setIsValidating(true);
        try {
          const validatePayload = createValidationPayload(gQuery, bQuery, obj);
          const err = await fetchSignalFlowAlertable(validatePayload);
          setValidationError(err);

          if (!isSignalFlowValidated(err)) {
            return undefined; // skip submitting
          }
        } catch (e) {
          onError(e as Error);
          return undefined; // skip submitting
        } finally {
          setIsValidating(false);
        }
      }

      setIsLoading(true);
      setResults(null);

      // Format datetime-local to ISO 8601 UTC
      const formatToISO8601UTC = (datetimeLocal: string) => {
        if (!datetimeLocal) return null;
        // "2026-01-14T00:00" becomes "2026-01-14T00:00:00Z"
        return `${datetimeLocal}:00Z`;
      };

      const fullData = Object.entries({
        ...formData,
        alertTypes: alertTypes.length > 0 ? alertTypes.map((option: any) => option.value) : null,
        startTime: formatToISO8601UTC(formData.startTime),
        endTime: formatToISO8601UTC(formData.endTime),
      }).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      const response = await apiFetch('/simulation/simulate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(fullData),
      });

      setResults({
        success: true,
        data: response,
      });

      return undefined;
    } catch (error) {
      onError(error as Error);

      // Extract error message from backend
      let errorMessage = 'An error occurred during simulation';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      setResults({
        success: false,
        error: errorMessage,
      });
      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ExperienceErrorBoundary>
      <WithPageTitle title="Simulate Alert">
        <div css={styles.root}>
          <Form<any> onSubmit={handleSubmit}>
            {({ formProps, getState }) => (
              <form {...formProps}>
                <FormHeader title="Simulate Alert">
                  <p>
                    Test SLO behavior with different alert types and time periods without affecting
                    production data
                  </p>
                </FormHeader>

                <FormSection>
                  <div css={styles.formFieldBlockStyles}>
                    <Label htmlFor="">
                      <div css={[flexCenter, spaceGapCompact]}>
                        <span css={styles.sfxIconStyles}>
                          <SfxIcon />
                        </span>
                        <span>SignalFlow Query</span>
                      </div>
                    </Label>
                    <Field
                      label={
                        <FormFieldTooltipLabel
                          label="Good Query"
                          content={helperCopy.slo.signalFlowGoodQueries}
                          isRequired
                        />
                      }
                      name="goodQuery"
                      defaultValue={sloData?.goodQuery || ''}
                      isRequired
                      validate={validateRequired}
                    >
                      {({ fieldProps }: any) => (
                        <div css={styles.textAreaField}>
                          <TextArea placeholder="Enter your good query..." {...fieldProps} />
                        </div>
                      )}
                    </Field>

                    {/* Good Query Validation Results */}
                    <QueryValidationResults
                      errors={validationError?.goodQueryErrors}
                      isValidating={isValidating}
                      queryType="good"
                    />

                    <Field
                      label={
                        <FormFieldTooltipLabel
                          label="Bad Query"
                          content={helperCopy.slo.signalFlowBadQueries}
                          isRequired
                        />
                      }
                      name="badQuery"
                      defaultValue={sloData?.badQuery || ''}
                      isRequired
                      validate={validateRequired}
                    >
                      {({ fieldProps }: any) => (
                        <div css={styles.textAreaField}>
                          <TextArea placeholder="Enter your bad query..." {...fieldProps} />
                        </div>
                      )}
                    </Field>

                    {/* Bad Query Validation Results */}
                    <QueryValidationResults
                      errors={validationError?.badQueryErrors}
                      isValidating={isValidating}
                      queryType="bad"
                    />

                    {/* Validation Button */}
                    <div css={{ marginTop: '16px' }}>
                      <LoadingButton
                        appearance="primary"
                        spacing="compact"
                        onClick={async () => {
                          const formValues = getState().values;
                          await handleValidate(
                            formValues.goodQuery || '',
                            formValues.badQuery || '',
                            formValues.objective || ''
                          );
                        }}
                        isLoading={isValidating}
                        isDisabled={isLoading}
                        shouldFitContainer
                      >
                        Validate SignalFlow Good &amp; Bad Queries
                      </LoadingButton>
                    </div>

                    {/* General Validation Errors */}
                    {/* eslint-disable react/no-array-index-key */}
                    {validationError &&
                      !isValidating &&
                      validationError.generalErrors &&
                      validationError.generalErrors.length > 0 && (
                        <ErrorMessage>
                          <div>
                            {validationError.generalErrors.map((err, idx) => (
                              <div key={`${err}-${idx}`}>{err}</div>
                            ))}
                          </div>
                        </ErrorMessage>
                      )}
                    {/* eslint-enable react/no-array-index-key */}
                  </div>

                  <div css={styles.formFieldBlockStyles}>
                    <Label htmlFor="">
                      <div css={[flexCenter, spaceGapCompact]}>
                        <GraphLineIcon label="Low traffic" size="small" />
                        <span>Low Traffic Options</span>
                      </div>
                    </Label>
                    <CheckboxField
                      name="suppressLowTrafficAlerts"
                      defaultIsChecked={!!sloData?.suppressLowTrafficAlerts}
                    >
                      {({ fieldProps }: any) => (
                        <div css={styles.checkboxField}>
                          <Checkbox
                            label={
                              <FormFieldTooltipLabel
                                label="Suppress Low Traffic Alerts"
                                content={helperCopy.slo.suppressLowTrafficAlerts}
                              />
                            }
                            {...fieldProps}
                          />
                        </div>
                      )}
                    </CheckboxField>
                    <Field
                      label={
                        <FormFieldTooltipLabel
                          label="Low Traffic Threshold"
                          content={helperCopy.slo.lowTrafficThreshold}
                        />
                      }
                      name="lowTrafficThreshold"
                      defaultValue={sloData?.lowTrafficThreshold || ''}
                    >
                      {({ fieldProps }: any) => (
                        <TextField
                          type="number"
                          min="0"
                          placeholder="Enter threshold..."
                          {...fieldProps}
                        />
                      )}
                    </Field>
                  </div>

                  <div css={styles.formFieldBlockStyles}>
                    <Label htmlFor="">
                      <div css={[flexCenter, spaceGapCompact]}>
                        <AlertIcon label="" size="small" />
                        <span>Alert Trigger Options</span>
                      </div>
                    </Label>
                    <Field
                      label={
                        <FormFieldTooltipLabel
                          label="SLO Target (%)"
                          content={helperCopy.slo.sloTarget}
                          isRequired
                        />
                      }
                      name="objective"
                      defaultValue={sloData?.objective || ''}
                      isRequired
                      validate={validateRequired}
                    >
                      {({ fieldProps }: any) => (
                        <TextField
                          type="number"
                          min="0"
                          max="99.9999"
                          step="0.0001"
                          {...fieldProps}
                        />
                      )}
                    </Field>
                    <div css={styles.selectField}>
                      <FormFieldTooltipLabel
                        label="Alert Type"
                        content="Alert types that can be simulated."
                        isRequired
                      />
                      <CheckboxSelect
                        options={ALERT_TYPE_OPTIONS}
                        isSearchable
                        placeholder="Select alert types..."
                        value={alertTypes}
                        onChange={(options: any) => {
                          setAlertTypes(options);
                          if (options && options.length > 0) setAlertTypeError('');
                        }}
                      />
                      {alertTypeError && <ErrorMessage>{alertTypeError}</ErrorMessage>}
                    </div>
                  </div>

                  <div css={styles.formFieldBlockStyles}>
                    <Label htmlFor="">
                      <div css={styles.labelWithSubtext}>
                        <div css={[flexCenter, spaceGapCompact]}>
                          <CalendarIcon label="" size="small" />
                          <span>Simulation Period</span>
                        </div>
                        <span>For optimal performance, keep the time range within 20 hours.</span>
                      </div>
                    </Label>
                    <div css={styles.dateRangeContainer}>
                      <Field
                        label={
                          <FormFieldTooltipLabel
                            label="Start Time (UTC)"
                            content="Start time for the simulation period."
                            isRequired
                          />
                        }
                        name="startTime"
                        defaultValue=""
                        isRequired
                        validate={validateRequired}
                      >
                        {({ fieldProps, error }: any) => (
                          <>
                            <TextField type="datetime-local" {...fieldProps} />
                            {error && <ErrorMessage>{error}</ErrorMessage>}
                          </>
                        )}
                      </Field>
                      <Field
                        label={
                          <FormFieldTooltipLabel
                            label="End Time (UTC)"
                            content="End time for the simulation period."
                            isRequired
                          />
                        }
                        name="endTime"
                        defaultValue=""
                        isRequired
                        validate={validateRequired}
                      >
                        {({ fieldProps, error }: any) => (
                          <>
                            <TextField type="datetime-local" {...fieldProps} />
                            {error && <ErrorMessage>{error}</ErrorMessage>}
                          </>
                        )}
                      </Field>
                    </div>
                  </div>
                </FormSection>

                <FormFooter align="start">
                  <ButtonGroup label="Form submit options">
                    <Button type="submit" appearance="primary">
                      Simulate
                    </Button>
                    <Button
                      appearance="subtle"
                      onClick={() => {
                        // If publicId exists, go back to that SLO's page, otherwise go home
                        navigate(publicIdParam ? `/slo/${publicIdParam}` : '/');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </FormFooter>
              </form>
            )}
          </Form>

          {/* Loading State */}
          {isLoading && (
            <div css={styles.loadingContainer}>
              <Spinner size="large" />
              <p>Simulating...</p>
            </div>
          )}

          {/* Results Section */}
          {results && !isLoading && (
            <div css={styles.resultsContainer}>
              {results.success ? (
                <div css={styles.successResults}>
                  <h3>Simulation Results</h3>
                  <p css={styles.alertDescriptionStyles}>Alert times in UTC</p>
                  {results.data?.alerts && Object.keys(results.data.alerts).length > 0 ? (
                    <div>
                      {Object.entries(results.data.alerts).map(([type, alerts]) => {
                        const alertList = Array.isArray(alerts) ? alerts : [];
                        const hasAlerts = alertList.length > 0;

                        return (
                          <div key={type} css={styles.alertTypeGroup}>
                            <div css={styles.alertTypeHeader}>
                              <h4 css={styles.alertTypeTitle}>
                                {ALERT_TYPE_OPTIONS.find(option => option.value === type)?.label ||
                                  type}
                              </h4>
                              <Lozenge>
                                {`${alertList.length} ${pluralize('alert', alertList.length)}`}
                              </Lozenge>
                            </div>

                            {hasAlerts ? (
                              <div css={styles.alertsGrid}>
                                {alertList.map((alert: any) => {
                                  const readable = formatDateForDisplay(
                                    alert.timestamp,
                                    DateDisplayFormats.TIME_WITH_NUMERIC_DATE
                                  );
                                  return (
                                    <div
                                      key={`${type}-${alert.timestamp}-${alert.tsId}`}
                                      css={styles.alertCard}
                                    >
                                      <div css={styles.alertTimeOnly}>
                                        <span role="img" aria-label="Alert detected">
                                          🚨
                                        </span>
                                        <span css={styles.alertTime}>{readable}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div css={styles.noAlertsContainer}>
                                <span css={styles.noAlertsIcon} role="img" aria-label="No alerts">
                                  ✅
                                </span>
                                <p>
                                  <strong>No Alerts Detected</strong>
                                </p>
                                <p css={styles.noAlertsSubtext}>
                                  The simulation completed successfully with no alerts triggered
                                  during the specified time period.
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div css={styles.noAlertsContainer}>
                      <span css={styles.noAlertsIcon} role="img" aria-label="No alerts">
                        ✅
                      </span>
                      <p>
                        <strong>No Alerts Detected</strong>
                      </p>
                      <p css={styles.noAlertsSubtext}>
                        The simulation completed successfully with no alerts triggered during the
                        specified time period.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div css={styles.errorResults}>
                  <h3>Simulation Failed</h3>
                  <p>{results.error}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </WithPageTitle>
    </ExperienceErrorBoundary>
  );
};

// Instrumented wrapper
const SimulationViewInstrumented: typeof SimulationView = props => {
  useRestartUFOComponentExperience(simulationViewExperience, []);

  return (
    <UFOExperienceContext.Provider value={simulationViewExperience}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <SimulationView {...props} />
    </UFOExperienceContext.Provider>
  );
};

export default SimulationViewInstrumented;
